import { useEffect, useMemo, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import axiosInstance from "../lib/axios";
import toast from "react-hot-toast";
import { Popconfirm } from "antd";
import NoData from "../Components/NoData";
import LoadingButton from "../Components/LoadingButton";
import useKeyboardDropdown from "../hooks/useKeyboardDropdown";
import DateSortHeader from "../Components/DateSortHeader";
import { formatDateLabel, sortByDateValue } from "../lib/dateFormat";
import { uppercasePayload } from "../lib/uppercasePayload";
import {
  validateDateInput,
  validateNumberInput,
  validateTextInput,
} from "../lib/formValidation";

const getLocalDateInputValue = (date = new Date()) => {
  const offsetMinutes = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offsetMinutes * 60 * 1000);
  return localDate.toISOString().slice(0, 10);
};

const toDateInputValue = (value) => {
  if (!value) {
    return getLocalDateInputValue();
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return getLocalDateInputValue();
  }

  const offsetMinutes = date.getTimezoneOffset();
  return new Date(date.getTime() - offsetMinutes * 60 * 1000)
    .toISOString()
    .slice(0, 10);
};

const normalizeString = (value) =>
  String(value || "")
    .toLowerCase()
    .trim();

const getPartyOptionKey = (item, kind) => {
  const name = normalizeString(item?.name);
  const code = normalizeString(
    kind === "customer" ? item?.customerCode : item?.vendorCode,
  );
  const phone = normalizeString(item?.contactInfo?.phone);

  if (name || code || phone) {
    return `${kind}:${name}|${code}|${phone}`;
  }

  const id = String(item?.id || "").trim();
  return id ? `${kind}:${id}` : `${kind}:unknown`;
};

const dedupeOptions = (items = [], kind) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getPartyOptionKey(item, kind);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [type, setType] = useState("received");
  const [partyType, setPartyType] = useState("customer");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [paidAt, setPaidAt] = useState(() => getLocalDateInputValue());
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [errors, setErrors] = useState({});
  const [customerQuery, setCustomerQuery] = useState("");
  const [vendorQuery, setVendorQuery] = useState("");
  const [query, setQuery] = useState("");
  const [showCustomerOptions, setShowCustomerOptions] = useState(false);
  const [showVendorOptions, setShowVendorOptions] = useState(false);
  const [paymentDateSort, setPaymentDateSort] = useState("desc");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const getId = (value) => value?.id ?? value?.id ?? value;

  const fetchPayments = async () => {
    try {
      const res = await axiosInstance.get("/payment");
      setPayments(res.data.payments || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load payments");
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [vendorsRes, customersRes] = await Promise.all([
        axiosInstance.get("/supplier"),
        axiosInstance.get("/customer"),
      ]);
      setVendors(vendorsRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchDropdownData();
  }, []);

  const validateField = (field, value, validator) => {
    const result = validator(value);
    setErrors((prev) => ({
      ...prev,
      [field]: result.ok ? "" : result.message,
    }));
    return result;
  };

  const resetPaymentForm = () => {
    setSelectedPayment(null);
    setType("received");
    setPartyType("customer");
    setAmount("");
    setMethod("cash");
    setPaidAt(getLocalDateInputValue());
    setDescription("");
    setCustomerId("");
    setVendorId("");
    setCustomerQuery("");
    setVendorQuery("");
    setShowCustomerOptions(false);
    setShowVendorOptions(false);
    setErrors({});
  };

  const openPaymentForm = (payment = null) => {
    setSelectedPayment(payment);
    setType(payment?.type || "received");
    setPartyType(
      payment?.partyType ||
        (payment?.type === "received" ? "customer" : "vendor"),
    );
    setAmount(payment?.amount ? String(payment.amount) : "");
    setMethod(payment?.method || "cash");
    setPaidAt(toDateInputValue(payment?.paidAt));
    setDescription(payment?.description || payment?.notes || "");
    setCustomerId(
      String(
        payment?.customerId?.id ||
          payment?.customer?.id ||
          payment?.customerId ||
          "",
      ),
    );
    setVendorId(String(payment?.vendor?.id || payment?.vendor || ""));
    setCustomerQuery(
      payment?.partyType === "customer"
        ? `${payment?.customerId?.name || payment?.customer?.name || "Customer"}${
            payment?.customerId?.customerCode || payment?.customer?.code
              ? ` (${payment?.customerId?.customerCode || payment?.customer?.code})`
              : ""
          }`
        : "",
    );
    setVendorQuery(
      payment?.partyType === "vendor"
        ? `${payment?.vendor?.name || "Vendor"}${
            payment?.vendor?.vendorCode ? ` (${payment.vendor.vendorCode})` : ""
          }`
        : "",
    );
    setShowCustomerOptions(false);
    setShowVendorOptions(false);
    setErrors({});
  };

  const handleTypeChange = (value) => {
    setType(value);
    if (value === "received") {
      setPartyType("customer");
      setVendorId("");
      setVendorQuery("");
      setShowVendorOptions(false);
    } else if (value === "paid") {
      setPartyType("vendor");
      setCustomerId("");
      setCustomerQuery("");
      setShowCustomerOptions(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    const uniqueCustomers = dedupeOptions(customers, "customer");
    const query = normalizeString(customerQuery);
    if (!query) return uniqueCustomers;

    return uniqueCustomers.filter((customer) => {
      const name = normalizeString(customer.name);
      const code = normalizeString(customer.customerCode);
      const phone = normalizeString(customer.contactInfo?.phone);
      return (
        name.includes(query) || code.includes(query) || phone.includes(query)
      );
    });
  }, [customers, customerQuery]);

  const filteredVendors = useMemo(() => {
    const uniqueVendors = dedupeOptions(vendors, "vendor");
    const query = normalizeString(vendorQuery);
    if (!query) return uniqueVendors;

    return uniqueVendors.filter((vendor) => {
      const name = normalizeString(vendor.name);
      const code = normalizeString(vendor.vendorCode);
      const phone = normalizeString(vendor.contactInfo?.phone);
      return (
        name.includes(query) || code.includes(query) || phone.includes(query)
      );
    });
  }, [vendors, vendorQuery]);

  const selectCustomer = (customer) => {
    const id = getId(customer);
    setCustomerId(id);
    setCustomerQuery(
      `${customer.name}${customer.customerCode ? ` (${customer.customerCode})` : ""}`,
    );
    setShowCustomerOptions(false);
  };

  const selectVendor = (vendor) => {
    const id = getId(vendor);
    setVendorId(id);
    setVendorQuery(
      `${vendor.name}${vendor.vendorCode ? ` (${vendor.vendorCode})` : ""}`,
    );
    setShowVendorOptions(false);
  };

  const {
    activeIndex: customerActiveIndex,
    onKeyDown: onCustomerKeyDown,
    setActiveIndex: setCustomerActiveIndex,
  } = useKeyboardDropdown({
    options: filteredCustomers,
    isOpen: showCustomerOptions && customerQuery.trim() !== "",
    onSelect: (customer) => selectCustomer(customer),
    onClose: () => setShowCustomerOptions(false),
  });

  const {
    activeIndex: vendorActiveIndex,
    onKeyDown: onVendorKeyDown,
    setActiveIndex: setVendorActiveIndex,
  } = useKeyboardDropdown({
    options: filteredVendors,
    isOpen: showVendorOptions && vendorQuery.trim() !== "",
    onSelect: (vendor) => selectVendor(vendor),
    onClose: () => setShowVendorOptions(false),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    const amountCheck = validateField("amount", amount, (value) =>
      validateNumberInput(value, "Amount", {
        min: 0.01,
        allowZero: false,
      }),
    );
    if (!amountCheck.ok) {
      toast.error(amountCheck.message);
      return;
    }

    if (
      partyType === "customer" &&
      !customerId &&
      !selectedPayment?.customerId?.name &&
      !selectedPayment?.customer?.name
    ) {
      toast.error("Customer is required");
      return;
    }

    if (partyType === "vendor" && !vendorId && !selectedPayment?.vendor?.id) {
      toast.error("Vendor is required");
      return;
    }

    const dateCheck = validateField("paidAt", paidAt, (value) =>
      validateDateInput(value, "Payment date"),
    );
    if (!dateCheck.ok) {
      toast.error(dateCheck.message);
      return;
    }

    const descriptionCheck = validateField(
      "description",
      description,
      (value) =>
        validateTextInput(value, "Description", {
          required: false,
          maxLength: 200,
          allowEmpty: true,
        }),
    );
    if (!descriptionCheck.ok) {
      toast.error(descriptionCheck.message);
      return;
    }

    const payload = {
      type,
      amount: amountCheck.value,
      method,
      partyType,
      paidAt: dateCheck.value,
      description: uppercasePayload(descriptionCheck.value),
    };

    if (partyType === "customer") {
      if (customerId) {
        payload.customerId = customerId;
      } else if (
        selectedPayment?.customerId?.name ||
        selectedPayment?.customer?.name
      ) {
        payload.customer = {
          code:
            selectedPayment?.customerId?.customerCode ||
            selectedPayment?.customer?.code ||
            "",
          name:
            selectedPayment?.customerId?.name ||
            selectedPayment?.customer?.name ||
            "",
        };
      }
    }

    if (partyType === "vendor") {
      payload.vendor = vendorId || selectedPayment?.vendor?.id || undefined;
    }

    try {
      setIsSubmitting(true);
      if (selectedPayment) {
        await axiosInstance.put(`/payment/${selectedPayment.id}`, payload);
        toast.success("Payment updated");
      } else {
        await axiosInstance.post("/payment", payload);
        toast.success("Payment recorded");
      }
      resetPaymentForm();
      fetchPayments();
    } catch (error) {
      console.error(error);
      toast.error(
        selectedPayment
          ? "Failed to update payment"
          : "Failed to record payment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (payment) => {
    openPaymentForm(payment);
  };

  const handleDelete = async (paymentId) => {
    try {
      await axiosInstance.delete(`/payment/${paymentId}`);
      toast.success("Payment deleted");
      if (selectedPayment?.id === paymentId) {
        resetPaymentForm();
      }
      fetchPayments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete payment");
    }
  };

  const getRowStyle = (paymentType = "") => {
    const normalizedType = String(paymentType).trim().toLowerCase();
    if (normalizedType === "received") {
      return "bg-amber-50/70 hover:bg-amber-100/80 border-amber-200";
    }
    if (normalizedType === "paid") {
      return "bg-emerald-50/70 hover:bg-emerald-100/80 border-emerald-200";
    }
    if (normalizedType === "debit") {
      return "bg-slate-50/80 hover:bg-slate-100 border-slate-200";
    }
    return "bg-white hover:bg-slate-50 border-slate-200";
  };

  const sortedPayments = useMemo(() => {
    return sortByDateValue(
      payments || [],
      (payment) => payment.paidAt || payment.createdAt,
      paymentDateSort,
    );
  }, [payments, paymentDateSort]);

  const filteredPayments = useMemo(() => {
    const normalizedQuery = normalizeString(query);
    if (!normalizedQuery) {
      return sortedPayments;
    }

    return sortedPayments.filter((payment) => {
      const typeText = normalizeString(payment.type);
      const partyText = normalizeString(
        payment.partyType === "vendor"
          ? [
              payment.vendor?.name,
              payment.vendor?.vendorCode,
              payment.vendor_code,
              "vendor",
            ]
              .filter(Boolean)
              .join(" ")
          : [
              payment.customerId?.name,
              payment.customer?.name,
              payment.customerId?.customerCode,
              payment.customer?.code,
              payment.customer_code,
              payment.customer_name,
              "customer",
            ]
              .filter(Boolean)
              .join(" "),
      );
      const amountText = normalizeString(
        `${Number(payment.amount || 0).toLocaleString("en-PK")} ${Number(payment.amount || 0)}`,
      );
      const dateText = normalizeString(
        `${formatDateLabel(payment.paidAt || payment.createdAt)} ${payment.paidAt || payment.createdAt || ""}`,
      );

      return (
        typeText.includes(normalizedQuery) ||
        partyText.includes(normalizedQuery) ||
        amountText.includes(normalizedQuery) ||
        dateText.includes(normalizedQuery)
      );
    });
  }, [query, sortedPayments]);

  return (
    <div className="min-h-[92vh] bg-gray-100 p-4">
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <h2 className="text-lg font-semibold mb-4">Record Payment</h2>
        {selectedPayment && (
          <div className="mb-4 flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <span>Editing payment #{selectedPayment.id}</span>
            <button
              type="button"
              onClick={resetPaymentForm}
              className="font-medium text-blue-700 hover:text-blue-900"
            >
              Cancel edit
            </button>
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          <div>
            <label className="text-sm font-medium">Type</label>
            <select
              value={type}
              onChange={(e) => {
                const value = e.target.value;
                handleTypeChange(value);
                validateField("type", value, (current) =>
                  validateTextInput(current, "Type", {
                    required: true,
                    maxLength: 20,
                  }),
                );
              }}
              className="w-full h-10 px-3 border rounded-xl mt-1"
            >
              <option value="received">Receive</option>
              <option value="paid">Pay</option>
              <option value="debit">Debit</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-500">{errors.type}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Method</label>
            <select
              value={method}
              onChange={(e) => {
                const value = e.target.value;
                setMethod(value);
                validateField("method", value, (current) =>
                  validateTextInput(current, "Method", {
                    required: true,
                    maxLength: 40,
                  }),
                );
              }}
              className="w-full h-10 px-3 border rounded-xl mt-1"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="card">Card</option>
              <option value="upi">UPI</option>
              <option value="paypal">PayPal</option>
              <option value="other">Other</option>
            </select>
            {errors.method && (
              <p className="mt-1 text-sm text-red-500">{errors.method}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Date</label>
            <input
              type="date"
              value={paidAt}
              onChange={(e) => {
                const value = e.target.value;
                setPaidAt(value);
                validateField("paidAt", value, (current) =>
                  validateDateInput(current, "Payment date"),
                );
              }}
              onBlur={(e) =>
                validateField("paidAt", e.target.value, (current) =>
                  validateDateInput(current, "Payment date"),
                )
              }
              className="w-full h-10 px-3 border rounded-xl mt-1"
            />
            {errors.paidAt && (
              <p className="mt-1 text-sm text-red-500">{errors.paidAt}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                const value = e.target.value;
                setAmount(value);
                validateField("amount", value, (current) =>
                  validateNumberInput(current, "Amount", {
                    min: 0.01,
                    allowZero: false,
                  }),
                );
              }}
              onBlur={(e) =>
                validateField("amount", e.target.value, (current) =>
                  validateNumberInput(current, "Amount", {
                    min: 0.01,
                    allowZero: false,
                  }),
                )
              }
              className="w-full h-10 px-3 border rounded-xl mt-1"
              required
              min="0"
              step="0.01"
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => {
                const value = e.target.value;
                setDescription(value);
                validateField("description", value, (current) =>
                  validateTextInput(current, "Description", {
                    required: false,
                    maxLength: 200,
                    allowEmpty: true,
                  }),
                );
              }}
              onBlur={(e) =>
                validateField("description", e.target.value, (current) =>
                  validateTextInput(current, "Description", {
                    required: false,
                    maxLength: 200,
                    allowEmpty: true,
                  }),
                )
              }
              className="w-full h-10 px-3 border rounded-xl mt-1"
              placeholder="Enter payment description"
              maxLength={200}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          {partyType === "customer" ? (
            <>
              <div className="relative">
                <label className="text-sm font-medium">Customer</label>
                <input
                  type="text"
                  value={customerQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomerQuery(value);
                    setCustomerId("");
                    setShowCustomerOptions(true);
                    validateField("customerQuery", value, (current) =>
                      validateTextInput(current, "Customer", {
                        required: true,
                        minLength: 2,
                        maxLength: 120,
                      }),
                    );
                  }}
                  onBlur={(e) => {
                    validateField("customerQuery", e.target.value, (current) =>
                      validateTextInput(current, "Customer", {
                        required: true,
                        minLength: 2,
                        maxLength: 120,
                      }),
                    );
                    setTimeout(() => {
                      setShowCustomerOptions(false);
                      setCustomerActiveIndex(-1);
                    }, 150);
                  }}
                  maxLength={120}
                  onFocus={() => {
                    setShowCustomerOptions(true);
                    setCustomerActiveIndex(0);
                  }}
                  onKeyDownCapture={onCustomerKeyDown}
                  className="w-full h-10 px-3 border rounded-xl mt-1"
                  placeholder="Search customer..."
                />
                {errors.customerQuery && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customerQuery}
                  </p>
                )}
                {showCustomerOptions && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border bg-white shadow">
                    {filteredCustomers.map((customer) => (
                      <button
                        key={getId(customer)}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                          customerActiveIndex ===
                          filteredCustomers.findIndex(
                            (item) => getId(item) === getId(customer),
                          )
                            ? "bg-slate-50"
                            : ""
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectCustomer(customer)}
                      >
                        {customer.name}
                        {customer.customerCode
                          ? ` (${customer.customerCode})`
                          : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="relative">
              <label className="text-sm font-medium">Vendor</label>
              <input
                type="text"
                value={vendorQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setVendorQuery(value);
                  setVendorId("");
                  setShowVendorOptions(true);
                  validateField("vendorQuery", value, (current) =>
                    validateTextInput(current, "Vendor", {
                      required: true,
                      minLength: 2,
                      maxLength: 120,
                    }),
                  );
                }}
                onBlur={(e) => {
                  validateField("vendorQuery", e.target.value, (current) =>
                    validateTextInput(current, "Vendor", {
                      required: true,
                      minLength: 2,
                      maxLength: 120,
                    }),
                  );
                  setTimeout(() => {
                    setShowVendorOptions(false);
                    setVendorActiveIndex(-1);
                  }, 150);
                }}
                maxLength={120}
                onFocus={() => {
                  setShowVendorOptions(true);
                  setVendorActiveIndex(0);
                }}
                onKeyDownCapture={onVendorKeyDown}
                className="w-full h-10 px-3 border rounded-xl mt-1"
                placeholder="Search vendor..."
              />
              {errors.vendorQuery && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.vendorQuery}
                </p>
              )}
              {showVendorOptions && filteredVendors.length > 0 && (
                <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border bg-white shadow">
                  {filteredVendors.map((vendor) => (
                    <button
                      key={getId(vendor)}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                        vendorActiveIndex ===
                        filteredVendors.findIndex(
                          (item) => getId(item) === getId(vendor),
                        )
                          ? "bg-slate-50"
                          : ""
                      }`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectVendor(vendor)}
                    >
                      {vendor.name}
                      {vendor.vendorCode ? ` (${vendor.vendorCode})` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="md:col-span-2">
            <LoadingButton
              type="submit"
              loading={isSubmitting}
              loadingText={selectedPayment ? "Updating..." : "Saving..."}
              className="w-full h-11 bg-teal-700 text-white rounded-xl hover:bg-teal-600"
            >
              {selectedPayment ? "Update Payment" : "Save Payment"}
            </LoadingButton>
          </div>
        </form>
      </div>
      <div className="my-4">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full md:w-96 h-10 px-4 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:outline-none"
          placeholder="Search by type, party, amount, or date..."
        />
      </div>
      <div className="mt-4 bg-white rounded-2xl shadow-sm border p-4">
        <h2 className="text-lg font-semibold mb-4">Payment History</h2>

        {filteredPayments.length === 0 ? (
          <NoData
            title={
              payments.length === 0 ? "No Payments" : "No Matching Payments"
            }
            description={
              payments.length === 0
                ? "Record a payment to see it listed here."
                : "Try adjusting the search to find matching payments."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-slate-500">
                  <DateSortHeader
                    label="Date"
                    direction={paymentDateSort}
                    onToggle={() =>
                      setPaymentDateSort((prev) =>
                        prev === "asc" ? "desc" : "asc",
                      )
                    }
                  />
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Party</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr
                    key={getId(payment)}
                    className={`border-b last:border-b-0 transition ${getRowStyle(
                      payment.type,
                    )}`}
                  >
                    <td className="px-4 py-3">
                      {formatDateLabel(payment.paidAt || payment.createdAt)}
                    </td>
                    <td className="px-4 py-3 capitalize">{payment.type}</td>
                    <td className="px-4 py-3">
                      Rs{Number(payment.amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {payment.description || payment.notes || "-"}
                    </td>
                    <td className="px-4 py-3">
                      {payment.partyType === "vendor"
                        ? payment.vendor?.name || "Vendor"
                        : payment.customerId?.name ||
                          payment.customer?.name ||
                          "Customer"}
                    </td>
                    <td className="px-4 py-3 capitalize">{payment.method}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditClick(payment)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-blue-100 text-blue-600 transition"
                          title="Edit Payment"
                        >
                          <MdEdit size={18} />
                        </button>

                        <Popconfirm
                          title={
                            <div className="flex flex-col gap-1 max-w-xs">
                              <span className="font-semibold text-red-600 text-sm">
                                Confirm Payment Deletion
                              </span>
                              <span className="text-xs text-gray-600 leading-snug">
                                This will remove the payment and update the
                                related customer or vendor ledger totals.
                              </span>
                            </div>
                          }
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{
                            danger: true,
                            className: "font-semibold",
                          }}
                          cancelButtonProps={{
                            className: "font-medium",
                          }}
                          placement="topRight"
                          onConfirm={() => handleDelete(payment.id)}
                        >
                          <button
                            type="button"
                            className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-red-600 transition"
                            title="Delete Payment"
                          >
                            <MdDelete size={18} />
                          </button>
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentsPage;
